import * as React from 'react';
import moment from 'moment';
import { cloneDeep, get, isArray, isEmpty, isNil, isObject, isString, omit, orderBy } from 'lodash';

import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';

import { __t, applyTranslations } from './language';
import { abbrNum } from './helper/common';
import { isDraft } from './subcampaign';
import { hasAccessToBrandProfile } from './organization';
import { getEntityFromProfile } from './view';

import { findMetricValue } from './metrics';
import { isMetricsStillFetching } from './helper/profile';
import {
  CHANNEL_TYPE_BLOG,
  CHANNEL_TYPE_FACEBOOK,
  CHANNEL_TYPE_INSTAGRAM,
  CHANNEL_TYPE_PINTEREST,
  CHANNEL_TYPE_SNAPCHAT,
  CHANNEL_TYPE_TIKTOK,
  CHANNEL_TYPE_TUMBLR,
  CHANNEL_TYPE_TWITTER,
  CHANNEL_TYPE_YOUTUBE,
  getUserNameFromChannel,
  isChannelHasEnoughFollowers
} from './channels';
import { INTERVAL } from './graphs/profile-graphs/profile-graphs';
import { insertIf } from './list';

import type {
  BarGraphDateInterval,
  ChannelAnalyticsScreenShotData,
  ChannelForm,
  Channels,
  CheckboxesListItem,
  ContentCardCreatorProfile,
  Deliverable,
  DeliverableType,
  Metrics,
  Organization,
  SubcampaignInfluencerProfile,
  TileStandard
} from 'types';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./i18n/influencer-utils.i18n.json'));
applyTranslations(require('modules/influencers/components/influencer-summary/influencer-profile-summary.i18n.json'));

const COMMON_MSG_KEY = 'collabary_common';

export const INFLUENCER_TYPE_STARS = 'stars';
export const INFLUENCER_TYPE_GIGA = 'giga';
export const INFLUENCER_TYPE_MEGA = 'mega';
export const INFLUENCER_TYPE_MACRO = 'macro';
export const INFLUENCER_TYPE_MEDIUM = 'medium';
export const INFLUENCER_TYPE_MICRO = 'micro';
export const INFLUENCER_TYPE_NANO = 'nano';

export const INFLUENCER_TYPES = [
  INFLUENCER_TYPE_STARS,
  INFLUENCER_TYPE_GIGA,
  INFLUENCER_TYPE_MEGA,
  INFLUENCER_TYPE_MACRO,
  INFLUENCER_TYPE_MEDIUM,
  INFLUENCER_TYPE_MICRO,
  INFLUENCER_TYPE_NANO
];

export const INFLUENCER_SELECTOR_AUTO_SELECT_DELAY = 150;

export const SPECIALIZED_IN_ITEM_LIMITS = {
  skills: 3
};

export const NUM_POSTS_VISIBLE = {
  instagram: 7,
  youtube: 4,
  tiktok: 5
};

export const GENDER_OPTIONS = ['F', 'M', 'G', 'N'];
export const GENDER_LABELS = {
  F: __t(`${COMMON_MSG_KEY}.gender.f`),
  M: __t(`${COMMON_MSG_KEY}.gender.m`),
  G: __t(`${COMMON_MSG_KEY}.gender.g`),
  N: __t(`${COMMON_MSG_KEY}.gender.n`)
};

export const GENDER_ADDITIONAL_OPTIONS = [
  'bigender',
  'cisgender_male',
  'cisgender_female',
  'genderfluid',
  'trans_man',
  'trans_woman',
  'self_describe',
  'no_answer'
];

export const GENDER_ADDITIONAL_LABELS = {
  bigender: __t(`${COMMON_MSG_KEY}.gender_additional.bigender`),
  cisgenderMale: __t(`${COMMON_MSG_KEY}.gender_additional.cisgender_male`),
  cisgenderFemale: __t(`${COMMON_MSG_KEY}.gender_additional.cisgender_female`),
  genderfluid: __t(`${COMMON_MSG_KEY}.gender_additional.genderfluid`),
  transMan: __t(`${COMMON_MSG_KEY}.gender_additional.trans_man`),
  transWoman: __t(`${COMMON_MSG_KEY}.gender_additional.trans_woman`),
  selfDescribe: __t(`${COMMON_MSG_KEY}.gender_additional.self_describe`),
  noAnswer: __t(`${COMMON_MSG_KEY}.gender_additional.no_answer`)
};

export const BODY_TYPE_MASS_OPTIONS = ['thin', 'muscular', 'average_build', 'curvy', 'plus_size'];
export const BODY_TYPE_HEIGHT_OPTIONS = ['petite', 'average_height', 'tall'];
export const BODY_ATTRIBUTES_OPTIONS = ['freckles', 'tattoos', 'piercings'];
export const ETHNIC_GROUP_OPTIONS = ['arabic', 'asian_pacific_islander', 'black', 'caucasian_white', 'hispanic_latino', 'indian_pakistani', 'multiracial', 'none'];
export const ETHNIC_GROUP_OPTIONS_PROFILE_BOOST = [
  'asian', 'black', 'hispanic', 'middle_eastern', 'multiracial', 'white', 'no_answer'
];
export const PROFESSION_OPTIONS = ['actor', 'musician', 'photographer', 'model', 'none'];
export const SPECIALIZED_IN_OPTIONS = [
  'fashion', 'beauty', 'technology', 'interior_design_decorating',
  'food_and_beverages', 'vegan_vegetarian', 'travel_and_retreats',
  'family', 'gaming', 'fitness_gym', 'outdoor_sports',
  'yoga_pilates_meditating', 'team_sports', 'pets'
];

export const FASHION_STYLES = [
  'athleisure', 'boho', 'bold', 'casual', 'classic', 'colorful', 'comfortable', 'elegant',
  'high_end', 'minimalistic', 'playful', 'preppy', 'romantic', 'sportswear', 'streetwear',
  'trendy', 'vintage', 'young'
];

export const FASHION_CATEGORIES = [
  'clothing', 'shoes', 'sports', 'accessories', 'beauty', 'streetwear',
  'designer', 'kids', 'pre_owned'
];

export const FASHION_SIZES = [
  'adaptive', 'petite', 'plus_size', 'standard_manufacturer_sizing', 'tall'
];

export const AUDIENCE_DATA_INFLUENCER_CHANNELS = [CHANNEL_TYPE_INSTAGRAM, CHANNEL_TYPE_TIKTOK];
export const ASSET_TYPES = {
  [CHANNEL_TYPE_INSTAGRAM]: 'influencer_instagram_audience_analytics',
  [CHANNEL_TYPE_TIKTOK]: 'influencer_tiktok_audience_analytics'
};
const AUDIENCE_DATA_EXPIRY_DAYS = 120;

export const PROFILE_TABS = [
  {
    label: __t('influencer_utils.influencer_profile_tab_about'),
    identifier: 'about',
    data: { type: 'activeTabMain' }
  },
  {
    label: __t('influencer_utils.influencer_profile_tab_instagram'),
    identifier: 'instagram',
    data: { type: 'activeTabMain' },
    icon: {
      name: 'instagram-custom-fg',
      width: 16,
      height: 16,
      isDynamic: true
    }
  },
  {
    label: __t('influencer_utils.influencer_profile_tab_tiktok'),
    identifier: CHANNEL_TYPE_TIKTOK,
    data: { type: 'activeTabMain' },
    icon: {
      name: 'tiktok-custom-fg',
      width: 16,
      height: 16,
      isDynamic: true
    }
  },
  {
    label: __t('influencer_utils.influencer_profile_tab_youtube'),
    identifier: 'youtube',
    data: { type: 'activeTabMain' },
    icon: {
      name: 'youtube-custom-fg',
      width: 16,
      height: 16,
      isDynamic: true
    }
  }
];

export const PROFILE_DETAILS_TABS = (channel) => [
  {
    label: (channel === CHANNEL_TYPE_YOUTUBE)
      ? __t('influencer_utils.influencer_profile_tab_subscriber_growth')
      : __t('influencer_utils.influencer_profile_tab_follower_growth'),
    identifier: 'followers',
    data: { type: 'activeTabDetails' }
  },
  {
    label: (channel === CHANNEL_TYPE_YOUTUBE || channel === CHANNEL_TYPE_TIKTOK)
      ? __t('influencer_utils.influencer_profile_tab_video_views')
      : __t('influencer_utils.influencer_profile_tab_engagement_rate'),
    identifier: (channel === CHANNEL_TYPE_YOUTUBE || channel === CHANNEL_TYPE_TIKTOK)
      ? 'videoViews'
      : 'engagementRate',
    data: { type: 'activeTabDetails' }
  },
  {
    label: __t('influencer_utils.influencer_profile_tab_top_hashtags'),
    identifier: 'overview',
    data: { type: 'activeTabDetails' }
  }
];

type ProfileGraphSelectionItem = {
  id: string,
  interval: BarGraphDateInterval,
  value: number,
  label: string
};

export const PROFILE_GRAPHS_DATE_SELECTION_ITEMS: Array<ProfileGraphSelectionItem> = [
  {
    id: 'one-month',
    interval: INTERVAL.WEEK,
    value: 30,
    label: __t('influencer_utils.date_selection.one_month')
  },
  {
    id: 'three-months',
    interval: INTERVAL.MONTH,
    value: 90,
    label: __t('influencer_utils.date_selection.three_months')
  },
  {
    id: 'six-months',
    interval: INTERVAL.MONTH,
    value: 180,
    label: __t('influencer_utils.date_selection.six_months')
  }, {
    id: 'one-year',
    interval: INTERVAL.YEAR,
    value: 365,
    label: __t('influencer_utils.date_selection.one_year')
  }
  // {
  //   id: 'all-time',
  //   interval: 'year',
  //   value: -1,
  //   label: t('influencer_profile.graphs.over_time.date_selection.all_time')
  // }
];

function parseChannelValueWithRegExp(url, regExp) {
  if (!url) {
    return url;
  }

  const parts = url.match(regExp);

  if (parts && parts.length === 2) {
    return parts[1];
  }

  return url;
}

export const getExpiredAnalyticsDataForInfluencer = (
  assets: Array<ChannelAnalyticsScreenShotData>,
  allowOnlyTiktok: boolean,
  forcedNotExpired?: boolean
): {
  [x: string]: boolean
} => {
  const expiredAnalyticsDataForInfluencer = AUDIENCE_DATA_INFLUENCER_CHANNELS.reduce((obj, channel) => {
    const channelAudienceData = assets.filter(asset => asset.assetType === ASSET_TYPES[channel]);
    const objCopy = { ...obj };

    if (forcedNotExpired) {
      objCopy[channel] = false;
      return objCopy;
    }
    if (channelAudienceData.length === 0) {
      objCopy[channel] = true;
    } else {
      // if logged in to FB, we don't show analytic upload section for instagram
      if (channel === 'instagram' && allowOnlyTiktok) {
        objCopy[channel] = false;
        return objCopy;
      }

      objCopy[channel] = moment().diff(moment(channelAudienceData[0].createdAt), 'days') > AUDIENCE_DATA_EXPIRY_DAYS;
    }

    return objCopy;
  }, {});

  return expiredAnalyticsDataForInfluencer;
};

export const INFLUENCER_CHANNELS = {
  instagram: {
    short: 'instagram.com/',
    long: 'https://www.instagram.com/',
    parseValue: (url) => parseChannelValueWithRegExp(url, /instagram\.com\/(.*)/i)
  },
  youtube: {
    short: '',
    long: '',
    parseValue: (url) => url
  },
  blog: {
    short: '',
    long: '',
    parseValue: (url) => url
  },
  twitter: {
    short: 'twitter.com/',
    long: 'https://twitter.com/',
    parseValue: (url) => parseChannelValueWithRegExp(url, /twitter\.com\/(.*)/i)
  },
  snapchat: {
    short: 'snapchat.com/add/',
    long: 'https://www.snapchat.com/add/',
    parseValue: (url) => parseChannelValueWithRegExp(url, /snapchat\.com\/add\/(.*)/i)
  },
  facebook: {
    short: 'facebook.com/',
    long: 'https://www.facebook.com/',
    parseValue: (url) => parseChannelValueWithRegExp(url, /facebook\.com\/(.*)/i)
  },
  pinterest: {
    short: 'pinterest.com/',
    long: 'https://www.pinterest.com/',
    parseValue: (url) => parseChannelValueWithRegExp(url, /pinterest\.com\/(.*)/i)
  },
  tumblr: {
    short: '',
    long: '',
    parseValue: (url) => url
  },
  tiktok: {
    short: 'tiktok.com/@',
    long: 'https://www.tiktok.com/@',
    parseValue: (url) => parseChannelValueWithRegExp(url, /tiktok\.com\/@(.*)/i)
  }
};

export const DELETION_REASON_BRAND_PROFILE = 'brand_profile';
export const DELETION_REASON_AGGREGATOR_PROFILE = 'aggregator_profile';
export const DELETION_REASON_LOW_QUALITY = 'low_quality';
export const DELETION_REASON_NOT_ACTIVE = 'not_active';

export const INFLUENCER_LIGHTWEIGHT_DELETION_REASON = [
  DELETION_REASON_BRAND_PROFILE,
  DELETION_REASON_AGGREGATOR_PROFILE,
  DELETION_REASON_LOW_QUALITY,
  DELETION_REASON_NOT_ACTIVE
];

export const SUPPORTED_INFLUENCER_CHANNELS = [CHANNEL_TYPE_INSTAGRAM, CHANNEL_TYPE_YOUTUBE, CHANNEL_TYPE_BLOG, CHANNEL_TYPE_TIKTOK];

export const INFLUENCER_CHANNELS_ORDER = [CHANNEL_TYPE_INSTAGRAM, CHANNEL_TYPE_TIKTOK, CHANNEL_TYPE_YOUTUBE, CHANNEL_TYPE_BLOG];
export const INFLUENCER_CHANNELS_ORDER_NEW = [
  CHANNEL_TYPE_INSTAGRAM,
  CHANNEL_TYPE_TIKTOK,
  CHANNEL_TYPE_YOUTUBE,
  CHANNEL_TYPE_BLOG,
  CHANNEL_TYPE_FACEBOOK,
  CHANNEL_TYPE_SNAPCHAT,
  CHANNEL_TYPE_PINTEREST,
  CHANNEL_TYPE_TWITTER,
  CHANNEL_TYPE_TUMBLR
];

// this method prepares java backend `channels` for use in the `calculateReach` method in utils/profile.js
export function prepareChannelsObjectForCalculateReach(influencer: TileStandard) {
  if (!isObject(influencer.channels)) {
    return influencer;
  }

  const channelsObject = influencer.channels;
  const influencerClone = cloneDeep(influencer);

  influencerClone.channels = [] as Channels;

  (Object.keys(channelsObject) as Array<keyof Channels>).forEach(channel => {
    const channelObject = channelsObject[channel];

    if (isObject(channelObject) && channelObject.url) {
      (influencerClone.channels as Array<any>).push({
        url: channelObject.url,
        type: channelObject.type,
        userName: channelObject.username,
        metrics: [{
          name: 'num_followers',
          value: channelObject.metrics?.followers
        }]
      });
    }
  });

  return influencerClone;
}

export function getShortlistsForInfluencer(campaigns, influencerId, organizations) {
  return orderBy(campaigns, [c => c.id], ['desc']).map((campaign) => {
    const influencerSubcampaign = campaign.subcampaigns
      .find(subcampaign => subcampaign.creatorId === influencerId);

    const isDraftSubcampaign = isDraft(influencerSubcampaign);
    const isSelected = Boolean(influencerSubcampaign);
    const hasAccess = hasAccessToBrandProfile(organizations, campaign.brandProfileId);
    const isDisabled = Boolean(influencerSubcampaign) && !isDraftSubcampaign || !hasAccess;
    const action = (isDisabled && Boolean(influencerSubcampaign))
      ? {
        label: __t('influencer_utils.button_go_to_offer'),
        link: `/campaigns/${campaign.id}/subcampaigns/${influencerSubcampaign.id}`
      }
      : {
        label: __t('influencer_utils.button_go_to_shortlist'),
        link: `/campaigns/${campaign.id}/participants/shortlisted`,
        hidden: !isSelected
      };

    return ({
      id: campaign.id,
      identifier: String(campaign.id),
      label: campaign.name,
      value: String(campaign.id),
      entity: getEntityFromProfile(campaign.brandProfile),
      isSelected,
      isDisabled,
      subcampaign: influencerSubcampaign,
      campaignId: campaign.id,
      action: {
        ...action,
        hidden: !hasAccess || action.hidden
      }
    }) as CheckboxesListItem;
  });
}

export const getTransformedChannels = (channels) => channels.map(channel => {
  const metrics = { ...channel.metricsV2 };

  // eslint-disable-next-line no-param-reassign
  delete channel.metricsV2;

  if (channel.type === CHANNEL_TYPE_TIKTOK) {
    return {
      ...channel,
      url: `${INFLUENCER_CHANNELS[CHANNEL_TYPE_TIKTOK].long}${channel.username}`,
      urlRaw: channel.url,
      metrics
    };
  }
  return { ...channel, metrics };
});

type ChannelInfo = Omit<ChannelForm, 'url'>;

export const getAllInfluecerAssetsData = (channels: Array<ChannelInfo>): Array<Omit<ChannelAnalyticsScreenShotData, 'id'>> => {
  let assets: Array<Omit<ChannelAnalyticsScreenShotData, 'id'>> = [];

  channels.forEach(channel => {
    assets = channel.assets?.length
      ? assets.concat([...channel.assets.map(asset => omit(asset, 'id'))])
      : assets.concat([]);
  });

  return assets;
};

export function getChannelValue(channel) {
  return INFLUENCER_CHANNELS[channel.type].parseValue(channel.url);
}

export function getFullChannelUrl(channelType, value) {
  if (!value) {
    return value;
  }

  let userInput = value;

  if (channelType === 'tiktok' && userInput.startsWith('@')) {
    userInput = userInput.substring(1);
  }

  return INFLUENCER_CHANNELS[channelType].long + // prefix
    userInput.replace(INFLUENCER_CHANNELS[channelType].long, ''); // value without prefix
}

export function isProfilePageComplete(influencerProfile) {
  const requiredFields = [
    'name',
    'profileImage',
    'influencerDateOfBirth',
    'influencerGender',
    'locations',
    'ethnicGroup'
  ];

  return !requiredFields.some(fieldName => !influencerProfile[fieldName] || influencerProfile[fieldName].length === 0);
}

export function getNumFollowers(followers) {
  return abbrNum(followers, { decimal: 1 }, true);
}

export function getInfluencerFullName(influencerProfile) {
  if (!influencerProfile || !influencerProfile.id) {
    return '';
  }

  return `${influencerProfile.influencerFirstName || ''} ${influencerProfile.influencerLastName || ''}`.trim();
}

export function getInfluencerLocationsPreview(locations) {
  if (isNil(locations)) {
    return null;
  }

  return locations.reduce((res, location) => {
    if (!isNil(location.city) && !isNil(location.countryCode)) {
      res.push(`${location.city}, ${location.countryCode}`);
    } else {
      // fallback to display something
      const locationName = get(location, 'city') || get(location, 'state') || get(location, 'country') || get(location, 'countryCode');

      if (locationName) {
        res.push(locationName);
      }
    }

    return res;
  }, []);
}

export function getPriceRecommendation(channels, channel = 'instagram'): Metrics {
  let priceRecommendation = {};

  if (isArray(channels)) {
    // Channels are in array format when they're not coming from search endpoint
    const metricsArray = channels.find(influencerChannel => influencerChannel.type === channel).metrics;
    const minPricePerPost = findMetricValue(metricsArray, 'min_price_per_post');
    const maxPricePerPost = findMetricValue(metricsArray, 'max_price_per_post');
    priceRecommendation = {
      minPricePerPost,
      maxPricePerPost
    };
  } else {
    // Channels are in map format when they're coming from search endpoint
    priceRecommendation = get(channels, `${channel}.metrics`, {});
  }

  return priceRecommendation;
}

export type PriceRecoItem = {
  minPrice?: number | null,
  maxPrice?: number | null,
  type: DeliverableType
}

export function getPriceRecoPerTasksTypes(channels, channel: string = 'instagram'): Array<PriceRecoItem> {
  const priceRecommendations: Array<PriceRecoItem> = [];

  if (isArray(channels)) {
    // Channels are in array format when they're not coming from search endpoint
    const metricsArray = channels.find(influencerChannel => influencerChannel.type === channel).metrics;

    if (channel === 'instagram') {
      priceRecommendations.push(
        {
          minPrice: findMetricValue(metricsArray, 'min_price_per_post'),
          maxPrice: findMetricValue(metricsArray, 'max_price_per_post'),
          type: 'instagram_post'
        },
        {
          minPrice: findMetricValue(metricsArray, 'min_price_per_story'),
          maxPrice: findMetricValue(metricsArray, 'max_price_per_story'),
          type: 'instagram_story'
        },
        {
          minPrice: findMetricValue(metricsArray, 'min_price_per_reel'),
          maxPrice: findMetricValue(metricsArray, 'max_price_per_reel'),
          type: 'instagram_reel'
        }
      );
    } else if (channel === 'tiktok') {
      priceRecommendations.push(
        {
          minPrice: findMetricValue(metricsArray, 'min_price_per_post'),
          maxPrice: findMetricValue(metricsArray, 'max_price_per_post'),
          type: 'tiktok_video'
        }
      );
    }
  } else {
    // Channels are in map format when they're coming from search endpoint
    const metricsV2: Metrics = get(channels, `${channel}.metricsV2`, undefined);
    const metrics: Metrics = get(channels, `${channel}.metrics`, undefined);
    const metricsToCheck = metricsV2 ?? metrics ?? {};

    if (channel === 'instagram') {
      priceRecommendations.push(
        {
          minPrice: metricsToCheck.minPricePerPost,
          maxPrice: metricsToCheck.maxPricePerPost,
          type: 'instagram_post'
        },
        {
          minPrice: metricsToCheck.minPricePerStory,
          maxPrice: metricsToCheck.maxPricePerStory,
          type: 'instagram_story'
        },
        {
          minPrice: metricsToCheck.minPricePerReel,
          maxPrice: metricsToCheck.maxPricePerReel,
          type: 'instagram_reel'
        }
      );
    } else if (channel === 'tiktok') {
      priceRecommendations.push(
        {
          minPrice: metricsToCheck.minPricePerPost,
          maxPrice: metricsToCheck.maxPricePerPost,
          type: 'tiktok_video'
        }
      );
    }
  }

  return priceRecommendations
    .filter(entry => Boolean(entry.minPrice || entry.maxPrice));
}

function hasChannel(channels, channelType) {
  const channel = channels.find(c => c.type === channelType && !isNil(c.id));

  return !isNil(channel);
}

export function getMostEngagedPosts(influencerProfile, channel) {
  return influencerProfile.mostEngagedPosts ? influencerProfile.mostEngagedPosts[channel] : [];
}

export function getMostRecentPosts(influencerProfile, channel) {
  return influencerProfile.mostRecentPosts ? influencerProfile.mostRecentPosts[channel] : [];
}

export function hasDataBeenGathered(influencer: TileStandard) {
  return Object.values(influencer.channels).some(channel => {
    if (!channel || !channel.metrics) {
      return false;
    }

    return Object.values(channel.metrics).some(Boolean);
  });
}

export function hasMetrics(channel) {
  const metrics = channel && channel.metrics;

  if (!metrics || Object.keys(metrics).length === 0) {
    return false;
  }

  return Object.keys(metrics).some((metricType) => (
    typeof metrics[metricType] === 'number' ? !isNil(metrics[metricType]) : false)
  );
}

function filterProfileTabs(channels, isFBButtonAllowedToShow) {
  const channelsWithEnoughFollowers = channels.filter(
    ch => isChannelHasEnoughFollowers(ch)
      || isMetricsStillFetching(ch.metrics) // also include channels which are still fetching
      || isFBButtonAllowedToShow
  );

  return PROFILE_TABS.filter(tab => {
    if (INFLUENCER_CHANNELS_ORDER.includes(tab.identifier)) {
      return hasChannel(channelsWithEnoughFollowers, tab.identifier);
    }

    return true;
  }).map(tab => {
    const channelInfo = channelsWithEnoughFollowers.find(channel => channel.type === tab.identifier);
    if (channelInfo && isMetricsStillFetching(channelInfo.metrics)) {
      // add gear icon to the tab label
      return {
        ...tab,
        label: (
          <React.Fragment>
            {tab.label}
            <span className="influencer-profile__tabs-label-icon">
              <SVGDynamicIcon
                name="two-gears-custom-fg"
                width={12}
                height={12}
              />
            </span>
          </React.Fragment>
        )
      };
    }

    return tab;
  });
}

export function getDefaultProfileTab(channels) {
  if (isNil(channels)) {
    return null;
  }

  const availableChannels = channels.filter(ch => isChannelHasEnoughFollowers(ch));
  const foundChannel = INFLUENCER_CHANNELS_ORDER.find(channel => (
    availableChannels.find(c => c.type === channel)
  ));

  return foundChannel && foundChannel !== 'blog' ? foundChannel : 'about';
}

export function getProfileTabs(
  channels,
  isBrand,
  isFreemium,
  isFBButtonAllowedToShow
) {
  const tabs = [
    ...filterProfileTabs(channels, isFBButtonAllowedToShow),
    ...insertIf(isBrand, {
      label: __t('influencer_utils.influencer_profile_tab_history'),
      identifier: 'history',
      data: { type: 'activeTabMain' },
      isDisabled: isFreemium
    })
  ];

  return [
    ...tabs.map(tab => ({
      ...tab,
      hash: tab.identifier
    }))
  ];
}

export function doesMatchSearchQuery(query, profile) {
  const searchQuery = query.trim().toLowerCase();
  const fields = [
    profile.name,
    profile.artistName,
    `${profile.influencerFirstName} ${profile.influencerLastName}`
  ];

  if (fields.some(field => (field || '').toLowerCase().indexOf(searchQuery) !== -1)) {
    return true;
  }

  const channels = profile.channels || {};

  return Object.keys(channels)
    .some(channel => channels[channel] && (getUserNameFromChannel(channels[channel]) || '').toLowerCase().indexOf(searchQuery) !== -1);
}

export function getFormattedFollowersValue(numberOfFollowers) {
  return (numberOfFollowers && numberOfFollowers < 1000)
    ? '< 1k'
    : numberOfFollowers;
}

export function mapResponseTypeToCategory(responseTime) {
  if (!responseTime) {
    return undefined;
  }

  if (responseTime.neverInvited) {
    return 'unknown';
  }

  const responseTimeInHours = responseTime.medianResponseOverallMinutes / 60;
  const responseTimeInDays = responseTimeInHours / 24;

  if (responseTimeInDays <= 2) {
    return 'quick';
  }

  if (responseTimeInDays <= 4) {
    return 'average';
  }

  const responseTimeInWeeks = responseTimeInDays / 7;

  if (responseTimeInWeeks <= 6) {
    return 'slow';
  }

  return 'not_responsive';
}

export const getAgeString = (age, ageRange, withComma = false) => {
  const updatedAge = ageRange && (isNil(age) || age === 0)
    ? ageRange
    : age;

  if (updatedAge) {
    return `${withComma ? ', ' : ''} ${updatedAge} ${__t('influencer_profile_summary.labels.years')}`.trim();
  }

  return '';
};

export const getCreatorNetworkInstagramUrl = (profileName: string): string => `https://instagram.com/${profileName}`;

export const getCreatorNetworkProfileUrl = (profileId: number | undefined | null, profileName: string): string => (!isNil(profileId)
  ? `/content-creator/profiles/${String(profileId)}`
  : getCreatorNetworkInstagramUrl(profileName));

export const getInfluencersDeliverableChannels = (deliverables: Array<Deliverable>) => {
  const channels: Array<{
    type: string,
    assets?: Array<ChannelAnalyticsScreenShotData>
  }> = [];
  const deliverableTypes = deliverables.map(deliverable => deliverable.deliverableType).join(',');

  AUDIENCE_DATA_INFLUENCER_CHANNELS.forEach(channel => {
    if (deliverableTypes.includes(channel)) {
      channels.push({
        type: channel,
        assets: []
      });
    }
  });

  return channels;
};

export const getInfluencerChannelUrl = (
  creatorProfile: Partial<ContentCardCreatorProfile | SubcampaignInfluencerProfile>,
  channel: string): string => {
  if (!Array.isArray(creatorProfile.channels)) {
    return creatorProfile.channels ? creatorProfile?.channels[channel]?.url : '';
  }

  return creatorProfile.channels.find(channelInfo => channelInfo.type === channel)?.url || '';
};

export const isMinimumCriteriaSelected = (
  relatedInfluencersSearchQuery
): boolean => !!Object.values(relatedInfluencersSearchQuery).filter(checkbox => checkbox).length;

export const getInfluencerProfileId = (
  organization?: Organization | null
): number | undefined | null => (
  organization
  && organization.influencerProfiles
  && organization.influencerProfiles[0]
  && organization.influencerProfiles[0].id || null
);

export const getProfileGraphsDateSelectionOptions = (
  startDate: string | null
): Array<ProfileGraphSelectionItem> => {
  if (isEmpty(startDate) || !isString(startDate)) {
    return [];
  }

  const today = moment();
  const diffDays = moment(today).diff(startDate, 'days');
  const diffMonths = diffDays / 30.417;

  const options = [
    ...PROFILE_GRAPHS_DATE_SELECTION_ITEMS
  ];

  // don't show "1 year"
  if (diffMonths < 6) {
    options.pop();
  }

  // don't show "6 months"
  if (diffMonths < 3) {
    options.pop();
  }

  // don't show "3 months"
  if (diffMonths < 1) {
    options.pop();
  }

  return options;
};
